'use client';

import { useCallback } from 'react';

import {
  faArrowTurnDownLeft,
  faArrowUp,
  faCircleNotch,
  faMicrophoneSlash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { MAX_RESPONSE_LENGTH } from '@/shared-question-model';
import {
  SharedSurveyVoiceInstruction,
  useProjectResponseModeContext,
} from '@/shared-survey';
import { QuestionType } from '@/survey-graphql';
import { AUDIO_AMPLIFIER, VoiceWaveform } from '@/ui';

import { useInputComposer } from './survey-ai-forms-form-section.hooks';
import type { Props } from './survey-ai-forms-input-composer.types';
import SurveyAiFormsVoiceInput from './survey-ai-forms-voice-input';

export default function SurveyAiFormsInputComposer({
  value,
  onChange,
  onSubmit,
  responseMode,
  disabled = false,
  isActive = true,
  onFocus,
  registerVoiceStopCallback,
  instanceId,
  isVoiceActive = false,
  canSubmit: canSubmitProp,
  isListening,
  questionType,
  loading = false,
  isMobile = false,
  isUserVoiceEnabled = false,
}: Props) {
  const t = useTranslations('AIForms.composer');
  const tAiForms = useTranslations('AIForms.aria');
  const tChat = useTranslations('Chat');
  const tVoice = useTranslations('Voice');

  const { audioLevelObservable, toggleResponseMode } =
    useProjectResponseModeContext();

  const {
    canSubmit,
    showNextText,
    isTextareaDisabled,
    textareaId,
    handleKeyDown,
    adjustHeight,
    attachActiveTextarea,
    attachTextarea,
  } = useInputComposer({
    value,
    onChange,
    onSubmit,
    responseMode,
    disabled,
    instanceId,
    isVoiceActive,
    canSubmit: canSubmitProp,
    isListening,
    questionType,
    loading,
  });

  const handleChange = useCallback(
    (next: string) => onChange?.(next.slice(0, MAX_RESPONSE_LENGTH)),
    [onChange]
  );

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(e.target.value),
    [handleChange]
  );

  const isOpenEnded = questionType === QuestionType.OpenEnded;
  const placeholder = isOpenEnded ? t('openEndedPlaceholder') : '';
  const isRecording = !!isListening;

  const handleCancelVoice = useCallback(() => {
    if (responseMode === 'voice') {
      toggleResponseMode();
    }
  }, [responseMode, toggleResponseMode]);

  const getSubmitAriaLabel = () => {
    if (isRecording) return tVoice('continue');
    if (showNextText) return tChat('next');
    return tAiForms('sendResponse');
  };

  return (
    <div
      data-test='input-composer'
      data-recording={isRecording}
      className='flex w-full flex-col gap-2'
      aria-label={t('inputComposerAria')}
    >
      <div
        className='flex w-full items-start justify-between'
        data-test='input-composer-input-area'
        aria-label={t('inputAreaAria')}
      >
        <div
          className='flex min-w-0 grow basis-0 items-start'
          data-test='input-composer-textarea-wrapper'
          aria-label={t('textareaWrapperAria')}
        >
          {isRecording ? (
            <VoiceWaveform
              isListening={isRecording}
              audioLevelSource={audioLevelObservable}
              audioLevelMultiplier={AUDIO_AMPLIFIER}
              color='var(--brand-primary)'
              barWidth={3}
              height={32}
              className='h-8 w-full'
            />
          ) : (
            <textarea
              id={textareaId}
              ref={
                isActive && isOpenEnded ? attachActiveTextarea : attachTextarea
              }
              value={value}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onInput={adjustHeight}
              placeholder={placeholder}
              className='h-(--textarea-height,24px) max-h-30 min-h-6 w-full flex-1 resize-none scrollbar-thin [scrollbar-color:rgba(31,41,55,0.32)_transparent] overflow-y-auto border-transparent bg-transparent p-0 [font-family:var(--brand-font)] text-base/6 font-normal text-(--brand-secondary) outline-hidden placeholder:text-(--brand-secondary) placeholder:opacity-60 hover:border-b-(--brand-primary) focus:border-none focus:border-b-(--brand-primary) focus:ring-0 focus:ring-offset-0 data-[height=auto]:h-auto md:h-(--textarea-height,30px) md:min-h-8 md:text-xl/8'
              rows={1}
              maxLength={MAX_RESPONSE_LENGTH}
              disabled={isTextareaDisabled}
              aria-label={t('textareaAria')}
              data-test='input-composer-textarea'
            />
          )}
        </div>
      </div>

      <div
        className='relative h-0 w-full shrink-0'
        data-test='input-composer-divider'
        aria-label={t('dividerAria')}
      >
        <div className='absolute inset-x-0 -top-px bottom-0 h-px bg-(--brand-primary)' />
      </div>

      {isMobile && isRecording && (
        <div
          className='flex w-full items-center justify-between gap-2 py-2'
          data-test='input-composer-mobile-voice-bar'
          aria-label={t('mobileVoiceBarAria')}
        >
          <SharedSurveyVoiceInstruction
            className='[font-family:var(--brand-font)] text-(--brand-secondary) opacity-[0.64]'
            textClassName='[font-family:var(--brand-font)] text-(--brand-secondary)'
            iconClassName='size-3.5 text-(--brand-secondary)'
          />
          <button
            type='button'
            onClick={handleCancelVoice}
            className='inline-flex h-8 items-center justify-center gap-2 rounded-full bg-gray-100 px-3 text-black transition-colors hover:bg-gray-200'
            aria-label={t('cancelRecordingAria')}
            data-test='input-composer-cancel-voice-mobile'
          >
            <FontAwesomeIcon
              icon={faMicrophoneSlash}
              className='size-3.5 text-inherit'
            />
            <span className='text-xs font-medium text-inherit'>
              {tChat('cancel')}
            </span>
          </button>
        </div>
      )}

      {!isMobile && (
        <div
          className='flex w-full items-center justify-end gap-2'
          data-test='input-composer-footer'
          aria-label={t('footerAria')}
        >
          <div
            className={cn(
              'hidden grow basis-0 items-center gap-6 opacity-[0.64] lg:flex',
              'text-(--brand-secondary)'
            )}
            aria-label={t('instructionsAria')}
          >
            {isRecording ? (
              <SharedSurveyVoiceInstruction
                className='[font-family:var(--brand-font)] text-(--brand-secondary)'
                textClassName='[font-family:var(--brand-font)] text-(--brand-secondary)'
                iconClassName='size-3.5 text-(--brand-secondary)'
              />
            ) : (
              isOpenEnded && (
                <>
                  <div
                    className='flex items-center gap-2'
                    aria-label={t('newLineHintAria')}
                    data-test='keyboard-shortcut-newline'
                  >
                    <div className='flex items-center gap-1'>
                      <div
                        className='flex items-center gap-1'
                        data-test='keyboard-shortcut-shift-key'
                      >
                        <p className='[font-family:var(--brand-font)] text-xs font-semibold'>
                          {t('shiftKey')}
                        </p>
                        <FontAwesomeIcon
                          icon={faArrowUp}
                          className='size-2.5'
                          data-test='keyboard-shortcut-shift-icon'
                        />
                      </div>
                      <p className='[font-family:var(--brand-font)] text-xs font-normal'>
                        +
                      </p>
                      <div
                        className='flex items-center gap-1'
                        data-test='keyboard-shortcut-enter-key'
                      >
                        <p className='[font-family:var(--brand-font)] text-xs font-semibold'>
                          {t('enterKey')}
                        </p>
                        <FontAwesomeIcon
                          icon={faArrowTurnDownLeft}
                          className='size-2.5'
                          data-test='keyboard-shortcut-enter-icon'
                        />
                      </div>
                    </div>
                    <p className='[font-family:var(--brand-font)] text-xs font-normal'>
                      {t('newLineAction')}
                    </p>
                  </div>

                  <div
                    className='flex items-center gap-2'
                    aria-label={t('submitHintAria')}
                    data-test='keyboard-shortcut-submit'
                  >
                    <div
                      className='flex items-center gap-1'
                      data-test='keyboard-shortcut-submit-key'
                    >
                      <p className='[font-family:var(--brand-font)] text-xs font-semibold'>
                        {t('enterKey')}
                      </p>
                      <FontAwesomeIcon
                        icon={faArrowTurnDownLeft}
                        className='size-2.5'
                        data-test='keyboard-shortcut-submit-icon'
                      />
                    </div>
                    <p className='[font-family:var(--brand-font)] text-xs font-normal'>
                      {t('submitAction')}
                    </p>
                  </div>
                </>
              )
            )}
          </div>

          {isRecording ? (
            <button
              type='button'
              onClick={handleCancelVoice}
              className='inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gray-100 px-4 text-black transition-colors hover:bg-gray-200'
              aria-label={t('cancelRecordingAria')}
              data-test='input-composer-cancel-voice'
            >
              <FontAwesomeIcon
                icon={faMicrophoneSlash}
                className='size-4 text-inherit'
              />
              <span className='text-sm font-medium text-inherit'>
                {tChat('cancel')}
              </span>
            </button>
          ) : (
            isUserVoiceEnabled && (
              <SurveyAiFormsVoiceInput
                value={value}
                isActive={isActive}
                onTranscriptChange={handleChange}
                onFocus={onFocus}
                registerStopCallback={registerVoiceStopCallback}
                instanceId={instanceId}
                hideLabel={true}
                className='size-10 rounded-full p-0'
                loading={loading}
                disabled={disabled}
                data-test='input-composer-voice-input'
              />
            )
          )}

          <button
            type='button'
            onClick={onSubmit}
            disabled={!canSubmit || loading}
            data-active={canSubmit && !loading}
            data-voice-active={(showNextText || isRecording) && !loading}
            data-loading={loading}
            className='flex h-10 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full bg-(--brand-bg-unselected) [font-family:var(--brand-font)] text-sm/4 font-medium text-black disabled:cursor-not-allowed data-[active=true]:bg-(--brand-primary) data-[active=true]:shadow-[0px_4px_24px_0px_rgba(52,52,111,0.24)] data-[voice-active=false]:w-10 data-[voice-active=true]:px-4'
            aria-label={getSubmitAriaLabel()}
            aria-disabled={!canSubmit || loading}
            data-test='input-composer-submit'
          >
            {loading ? (
              <FontAwesomeIcon
                icon={faCircleNotch}
                className='size-4 animate-spin text-(--brand-primary)'
                aria-label={t('loadingIconAria')}
                data-test='input-composer-loading-icon'
              />
            ) : (
              <>
                {(showNextText || isRecording) && (
                  <span
                    className='text-(--brand-primary-contrast)'
                    data-test='input-composer-next-text'
                  >
                    {isRecording ? tVoice('continue') : tChat('next')}
                  </span>
                )}
                <FontAwesomeIcon
                  icon={faArrowUp}
                  className={cn(
                    'size-3 rotate-90',
                    (canSubmit && !loading) || showNextText || isRecording
                      ? 'text-(--brand-primary-contrast)'
                      : 'text-(--brand-text-unselected)'
                  )}
                  data-test='next-arrow'
                />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
